/* eslint-disable @typescript-eslint/ban-ts-ignore */
import { inject, injectable } from 'inversify';
import { Decimal } from 'decimal.js';
import {
    IExpressionEvaluator,
    QuinoCoreServiceSymbols,
    IExpression,
    isExpression, IBinaryExpression, ExpressionOperator,
} from '@quino/core';

export const isBinaryExpression = (object: IExpression): object is IBinaryExpression => {
    return (
        (object as IBinaryExpression).Operator !== undefined &&
        (object as IBinaryExpression).left !== undefined &&
        (object as IBinaryExpression).right !== undefined
    );
};

@injectable()
export class PerformaBinaryExpressionEvaluator implements IExpressionEvaluator {
    constructor(@inject(QuinoCoreServiceSymbols.IExpressionEvaluator) private evaluator: IExpressionEvaluator) {}

    evaluate<TValue>(expression: IExpression | TValue, context: any): TValue {
        if (isExpression(expression) && isBinaryExpression(expression)) {
            const leftResult: any = this.evaluator.evaluate(expression.left, context);
            const rightResult: any = expression.right != null ? this.evaluator.evaluate(expression.right, context) : null;

            switch (parseInt(expression.Operator.toString(), undefined)) {
                case ExpressionOperator.Add:
                    // @ts-ignore
                    return new Decimal(leftResult).add(rightResult).toNumber();
                case ExpressionOperator.Subtract:
                    // @ts-ignore
                    return new Decimal(leftResult).minus(rightResult).toNumber();
                case ExpressionOperator.Divide:
                    // @ts-ignore
                    return new Decimal(leftResult).dividedBy(rightResult).toNumber();
                case ExpressionOperator.Multiply:
                    // @ts-ignore
                    return new Decimal(leftResult).times(rightResult).toNumber();
                case ExpressionOperator.Mod:
                    // @ts-ignore
                    return new Decimal(leftResult).modulo(rightResult).toNumber();
                case ExpressionOperator.Power:
                    // @ts-ignore
                    return new Decimal(leftResult).toPower(rightResult).toNumber();
                case ExpressionOperator.Negate:
                    // @ts-ignore
                    return !(leftResult && rightResult);
                case ExpressionOperator.Equal:
                    // @ts-ignore
                    return leftResult === rightResult;
                case ExpressionOperator.NotEqual:
                    // @ts-ignore
                    return leftResult !== rightResult;
                case ExpressionOperator.LessThan:
                    // @ts-ignore
                    return leftResult < rightResult;
                case ExpressionOperator.LessThanEqual:
                    // @ts-ignore
                    return leftResult <= rightResult;
                case ExpressionOperator.GreaterThan:
                    // @ts-ignore
                    return leftResult > rightResult;
                case ExpressionOperator.GreaterThanEqual:
                    // @ts-ignore
                    return leftResult >= rightResult;
                case ExpressionOperator.In:
                    throw new Error('Can not map [In] operator.');
                case ExpressionOperator.And:
                    return leftResult && rightResult;
                case ExpressionOperator.Or:
                    return leftResult || rightResult;
                case ExpressionOperator.Not:
                    // @ts-ignore
                    return !leftResult;
                case ExpressionOperator.Concatenate:
                    throw new Error('[Concatenate] operator is not yet implemented.');
                case ExpressionOperator.Coalesce:
                    return leftResult != null ? leftResult : rightResult;
                case ExpressionOperator.IsNull:
                    // @ts-ignore
                    return leftResult == null && rightResult == null;
                case ExpressionOperator.IsNotNull:
                    // @ts-ignore
                    return leftResult != null;
                case ExpressionOperator.FormatGroups:
                    throw new Error('[FormatGroups] operator is not yet implemented.');
                case ExpressionOperator.FormattedAs:
                    throw new Error('[FormattedAs] operator is not yet implemented.');
                case ExpressionOperator.BeginsWith:
                    return leftResult != null && leftResult.toString().startsWith(rightResult);
                case ExpressionOperator.EndsWith:
                    return leftResult != null && leftResult.toString().endsWith(rightResult);
                case ExpressionOperator.Contains:
                    return leftResult != null && leftResult.toString().includes(rightResult);
                case ExpressionOperator.Matches:
                    return leftResult != null && leftResult.toString().includes(rightResult);
                case ExpressionOperator.EqualCI:
                    // @ts-ignore
                    return this.equalsCi(leftResult, rightResult);
                case ExpressionOperator.NotEqualCI:
                    // @ts-ignore
                    return !this.equalsCi(leftResult, rightResult);
                case ExpressionOperator.BeginsWithCI:
                    return leftResult != null && leftResult.toString().startsWith(rightResult);
                case ExpressionOperator.EndsWithCI:
                    return leftResult != null && leftResult.toString().endsWith(rightResult);
                case ExpressionOperator.ContainsCI:
                    return leftResult != null && leftResult.toString().includes(rightResult);
                case ExpressionOperator.MatchesCI:
                    if (rightResult == null) {
                        // @ts-ignore
                        return false;
                    }
                    return (
                        leftResult != null &&
                        leftResult
                            .toString()
                            .toLowerCase()
                            .includes(rightResult.toString().toLowerCase())
                    );
                default:
                    throw new Error('Unexpected operator while parsing [BinaryExpression].');
            }
        }

        // @ts-ignore
        return expression;
    }

    private equalsCi = (a: any, b: any) => {
        return typeof a === 'string' && typeof b === 'string' ? a.localeCompare(b, undefined, { sensitivity: 'accent' }) === 0 : a === b;
    };
}
